import { useAssetVizInternal } from 'features/assetViz/AssetVizInternalProvider'
import {
  BoundsCalculator,
  DefaultCamera,
  FitCalculator,
  PositionCalculator,
  SelectionCallback,
} from 'features/assetViz/cameras/DefaultCamera'
import { DomeSurface3D } from 'features/assetViz/objects/surfaces/DomeSurface3D'
import { DomeWallSurface3D } from 'features/assetViz/objects/surfaces/DomeWallSurface3D'
import { FlatCircularFloorSurface3D } from 'features/assetViz/objects/surfaces/FlatCircularFloorSurface3D'
import { SceneFactoryConfig } from 'features/assetViz/scenes/sceneFactory'
import React from 'react'
import {
  GeckoAssetModelType,
  GeckoComponentModelType,
  GeckoComponentRect,
  GeckoDome,
  GeckoDomeWall,
  GeckoEditableComponent,
  GeckoFlatCircularFloor,
} from 'types'

export const ContainmentDomeSceneConfig: SceneFactoryConfig = {
  name: 'ContainmentDome',
  assetModels: [GeckoAssetModelType.NuclearContainmentDome],
  componentModels: [
    GeckoComponentModelType.Dome,
    GeckoComponentModelType.DomeWall,
    GeckoComponentModelType.FlatCircularFloor,
  ],
  sceneFunc: (props) => <ContainmentDomeScene {...props} />,
}
/**
 * A scene depicting a Nuclear Containment Dome that wraps around the y-axis.
 * @param componentModel  The model for the component in question
 */
export const ContainmentDomeScene: React.FC<{
  componentModel: GeckoEditableComponent
}> = ({ componentModel }) => {
  const selectedModel = componentModel as
    | GeckoDome
    | GeckoDomeWall
    | GeckoFlatCircularFloor

  //----------------------------------------------------------------------------
  const { show3D, componentDataFrames } = useAssetVizInternal()

  const componentBounds = React.useMemo(() => {
    if (
      selectedModel.type === GeckoComponentModelType.Dome ||
      selectedModel.type === GeckoComponentModelType.FlatCircularFloor
    ) {
      const radius = selectedModel.radius.num
      return GeckoComponentRect.CreateFromXYs(
        selectedModel.componentBounds.componentID,
        -radius,
        -radius,
        radius,
        radius,
        selectedModel.componentBounds.y.unit,
        selectedModel.componentBounds.y.unit
      )
    }
    const componentBounds = selectedModel.componentBounds
    return componentBounds
  }, [selectedModel.componentBounds, selectedModel.type, selectedModel.radius])

  const tankRadius = React.useMemo(() => selectedModel.radius, [selectedModel])

  const onComponentSelected = React.useCallback<SelectionCallback>(
    () =>
      // This starts the camera off wherever there's data to look at
      selectedModel.type === GeckoComponentModelType.Dome
        ? componentBounds
        : (componentDataFrames
            ?.at(0)
            ?.data?.geometry?.project(componentBounds.space) ??
          componentBounds),
    [componentDataFrames, componentBounds, selectedModel.type]
  )

  const getCameraBounds = React.useCallback<BoundsCalculator>(
    () => ({
      rect: componentBounds,
      doWrapX:
        show3D &&
        (selectedModel.type === GeckoComponentModelType.DomeWall ||
          selectedModel.type === GeckoComponentModelType.Dome),
    }),
    [componentBounds, show3D, selectedModel.type]
  )

  const getCameraPosition = React.useCallback<PositionCalculator>(
    ({ componentX, componentY, distToComponent }) => {
      const relX = componentX - componentBounds.start.x.num
      const relY = componentY - componentBounds.start.y.num

      if (selectedModel.type === GeckoComponentModelType.FlatCircularFloor) {
        return {
          position: [componentX, componentY, distToComponent],
          rotation: [0, 0, 0],
        }
      } else if (selectedModel.type === GeckoComponentModelType.Dome) {
        if (!show3D) {
          return {
            position: [componentX, componentY, distToComponent],
            rotation: [0, 0, 0],
          }
        }

        // Dome in 3D mode
        const angle =
          2.0 *
            Math.PI *
            ((componentX + tankRadius.num) / (tankRadius.num * 2)) -
          Math.PI / 2 // Add 90 degrees to make it face 0

        const distToAxis = tankRadius.num + distToComponent

        return {
          position: [
            Math.sin(angle) * distToAxis,
            componentY,
            Math.cos(angle) * distToAxis,
          ],
          rotation: [0, angle, 0],
        }
      }

      // DomeWall handling
      if (!show3D) {
        return {
          position: [relX, relY, distToComponent],
          rotation: [0, 0, 0],
        }
      }

      // 3D mode for DomeWall
      // Also remove the -0.25 offset here for consistency
      const angle = 2.0 * Math.PI * (relX / (tankRadius.num * 2 * Math.PI))
      const distToAxis = tankRadius.num + distToComponent

      return {
        position: [
          Math.sin(angle) * distToAxis,
          relY,
          Math.cos(angle) * distToAxis,
        ],
        rotation: [0, angle, 0],
      }
    },
    [componentBounds, tankRadius.num, show3D, selectedModel.type]
  )

  const getCameraDimensionsFit = React.useCallback<FitCalculator>(
    (rect) =>
      show3D
        ? [Math.min(rect.width.num, tankRadius.num * 2.0), rect.height.num]
        : [rect.width.num, rect.height.num],
    [show3D, tankRadius]
  )

  //----------------------------------------------------------------------------

  return !selectedModel ? null : (
    <>
      <DefaultCamera
        onComponentSelected={onComponentSelected}
        getCameraBounds={getCameraBounds}
        getCameraPosition={getCameraPosition}
        getCameraDimensionsFit={getCameraDimensionsFit}
      />
      {selectedModel instanceof GeckoDomeWall ? (
        <DomeWallSurface3D model={selectedModel} />
      ) : selectedModel instanceof GeckoDome ? (
        <DomeSurface3D model={selectedModel} />
      ) : selectedModel instanceof GeckoFlatCircularFloor ? (
        <FlatCircularFloorSurface3D model={selectedModel} />
      ) : null}
    </>
  )
}
